import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { getLabelDefaults, updateLabelDefaults } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Settings, Save, RotateCcw } from "lucide-react";

const LabelDefaults = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState({
    serving_size_g: 50,
    servings_per_pack: 40,
    allergen_text: "Milled in a factory that also processes grains which contain gluten, soy, and nuts.",
    footnote_text: "*Percent Recommended Daily Allowance (RDA) are based on a 2000 calorie diet. Your daily values may be higher or lower depending on your calorie needs.",
    serving_size_label: "Serving Size",
    per_100g_label: "Per 100g",
    per_serve_label: "Per Serve",
    rda_label: "% RDA*",
    servings_per_pack_label: "Servings per Pack",
    nutrient_label: "Nutrient",
    ingredients_heading: "INGREDIENTS:",
    allergen_heading: "ALLERGEN INFORMATION:",
    // RDA visibility settings
    show_rda_energy: true,
    show_rda_protein: true,
    show_rda_carbohydrate: true,
    show_rda_fibre: true,
    show_rda_fat: true,
    show_rda_saturated_fat: true,
    show_rda_sodium: true
  });

  const loadDefaults = useCallback(async () => {
    try {
      const data = await getLabelDefaults();
      setDefaults(data);
    } catch (error) {
      toast.error("Failed to load label defaults");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDefaults();
  }, [loadDefaults]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateLabelDefaults(defaults);
      toast.success("Label defaults saved successfully");
    } catch (error) {
      toast.error("Failed to save label defaults");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDefaults({
      serving_size_g: 50,
      servings_per_pack: 40,
      allergen_text: "Milled in a factory that also processes grains which contain gluten, soy, and nuts.",
      footnote_text: "*Percent Recommended Daily Allowance (RDA) are based on a 2000 calorie diet. Your daily values may be higher or lower depending on your calorie needs.",
      serving_size_label: "Serving Size",
      per_100g_label: "Per 100g",
      per_serve_label: "Per Serve",
      rda_label: "% RDA*",
      servings_per_pack_label: "Servings per Pack",
      nutrient_label: "Nutrient",
      ingredients_heading: "INGREDIENTS:",
      allergen_heading: "ALLERGEN INFORMATION:",
      show_rda_energy: true,
      show_rda_protein: true,
      show_rda_carbohydrate: true,
      show_rda_fibre: true,
      show_rda_fat: true,
      show_rda_saturated_fat: true,
      show_rda_sodium: true
    });
    toast.info("Reset to default values (not saved yet)");
  };

  const updateField = (field, value) => {
    setDefaults(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5D4037]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="label-defaults-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-[#5D4037] mb-2">Label Defaults</h1>
            <p className="text-[#795548]">Set default values for all new nutrition labels</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleReset}
              className="border-[#5D4037] text-[#5D4037]"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button
              data-testid="save-defaults-button"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#5D4037] text-white hover:bg-[#4E342E]"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Defaults"}
            </Button>
          </div>
        </div>

        <div className="space-y-8">
          {/* Serving Defaults */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D7CCC8]/50 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-[#EFEBE9] rounded-lg">
                <Settings className="w-5 h-5 text-[#5D4037]" />
              </div>
              <h2 className="text-xl font-semibold text-[#5D4037]">Serving Defaults</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="serving_size_g">Default Serving Size (g)</Label>
                <Input
                  id="serving_size_g"
                  data-testid="serving-size-input"
                  type="number"
                  value={defaults.serving_size_g}
                  onChange={(e) => updateField('serving_size_g', parseFloat(e.target.value) || 0)}
                  className="mt-1"
                />
                <p className="text-xs text-[#795548] mt-1">Used to calculate "Per Serve" values</p>
              </div>
              
              <div>
                <Label htmlFor="servings_per_pack">Default Servings per Pack</Label>
                <Input
                  id="servings_per_pack"
                  data-testid="servings-per-pack-input"
                  type="number"
                  value={defaults.servings_per_pack || ''}
                  onChange={(e) => updateField('servings_per_pack', e.target.value ? parseFloat(e.target.value) : null)}
                  className="mt-1"
                />
                <p className="text-xs text-[#795548] mt-1">Number of servings in each package</p>
              </div>
            </div>
          </div>

          {/* Label Column Headers */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D7CCC8]/50 p-6">
            <h2 className="text-xl font-semibold text-[#5D4037] mb-6">Table Column Labels</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="nutrient_label">Nutrient Column</Label>
                <Input
                  id="nutrient_label"
                  value={defaults.nutrient_label}
                  onChange={(e) => updateField('nutrient_label', e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="per_100g_label">Per 100g Column</Label>
                <Input
                  id="per_100g_label"
                  value={defaults.per_100g_label}
                  onChange={(e) => updateField('per_100g_label', e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="per_serve_label">Per Serve Column</Label>
                <Input
                  id="per_serve_label"
                  value={defaults.per_serve_label}
                  onChange={(e) => updateField('per_serve_label', e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="rda_label">% RDA Column</Label>
                <Input
                  id="rda_label"
                  value={defaults.rda_label}
                  onChange={(e) => updateField('rda_label', e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="serving_size_label">Serving Size Label</Label>
                <Input
                  id="serving_size_label"
                  value={defaults.serving_size_label}
                  onChange={(e) => updateField('serving_size_label', e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="servings_per_pack_label">Servings per Pack Label</Label>
                <Input
                  id="servings_per_pack_label"
                  value={defaults.servings_per_pack_label}
                  onChange={(e) => updateField('servings_per_pack_label', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Section Headings */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D7CCC8]/50 p-6">
            <h2 className="text-xl font-semibold text-[#5D4037] mb-6">Section Headings</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ingredients_heading">Ingredients Heading</Label>
                <Input
                  id="ingredients_heading"
                  value={defaults.ingredients_heading}
                  onChange={(e) => updateField('ingredients_heading', e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="allergen_heading">Allergen Heading</Label>
                <Input
                  id="allergen_heading"
                  value={defaults.allergen_heading}
                  onChange={(e) => updateField('allergen_heading', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Default Text Content */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D7CCC8]/50 p-6">
            <h2 className="text-xl font-semibold text-[#5D4037] mb-6">Default Text Content</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="allergen_text">Default Allergen Text</Label>
                <Textarea
                  id="allergen_text"
                  data-testid="allergen-text-input"
                  value={defaults.allergen_text}
                  onChange={(e) => updateField('allergen_text', e.target.value)}
                  rows={2}
                  className="mt-1"
                />
                <p className="text-xs text-[#795548] mt-1">Pre-filled allergen warning for new labels</p>
              </div>
              
              <div>
                <Label htmlFor="footnote_text">Default Footnote Text</Label>
                <Textarea
                  id="footnote_text"
                  data-testid="footnote-text-input"
                  value={defaults.footnote_text}
                  onChange={(e) => updateField('footnote_text', e.target.value)}
                  rows={3}
                  className="mt-1"
                />
                <p className="text-xs text-[#795548] mt-1">RDA disclaimer shown at bottom of label</p>
              </div>
            </div>
          </div>

          {/* RDA Visibility Settings */}
          <div className="bg-white rounded-xl shadow-sm border border-[#D7CCC8]/50 p-6">
            <h2 className="text-xl font-semibold text-[#5D4037] mb-2">% RDA Display Settings</h2>
            <p className="text-sm text-[#795548] mb-6">Choose which nutrients show the % RDA column on labels</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div className="flex items-center justify-between p-3 bg-[#FAF9F6] rounded-lg">
                <Label htmlFor="show_rda_energy" className="cursor-pointer">Energy</Label>
                <Switch
                  id="show_rda_energy"
                  checked={defaults.show_rda_energy !== false}
                  onCheckedChange={(checked) => updateField('show_rda_energy', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-[#FAF9F6] rounded-lg">
                <Label htmlFor="show_rda_protein" className="cursor-pointer">Protein</Label>
                <Switch
                  id="show_rda_protein"
                  checked={defaults.show_rda_protein !== false}
                  onCheckedChange={(checked) => updateField('show_rda_protein', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-[#FAF9F6] rounded-lg">
                <Label htmlFor="show_rda_carbohydrate" className="cursor-pointer">Carbohydrate</Label>
                <Switch
                  id="show_rda_carbohydrate"
                  checked={defaults.show_rda_carbohydrate !== false}
                  onCheckedChange={(checked) => updateField('show_rda_carbohydrate', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-[#FAF9F6] rounded-lg">
                <Label htmlFor="show_rda_fibre" className="cursor-pointer">Dietary Fibre</Label>
                <Switch
                  id="show_rda_fibre"
                  checked={defaults.show_rda_fibre !== false}
                  onCheckedChange={(checked) => updateField('show_rda_fibre', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-[#FAF9F6] rounded-lg">
                <Label htmlFor="show_rda_fat" className="cursor-pointer">Total Fat</Label>
                <Switch
                  id="show_rda_fat"
                  checked={defaults.show_rda_fat !== false}
                  onCheckedChange={(checked) => updateField('show_rda_fat', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-[#FAF9F6] rounded-lg">
                <Label htmlFor="show_rda_saturated_fat" className="cursor-pointer">Saturated Fat</Label>
                <Switch
                  id="show_rda_saturated_fat"
                  checked={defaults.show_rda_saturated_fat !== false}
                  onCheckedChange={(checked) => updateField('show_rda_saturated_fat', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-[#FAF9F6] rounded-lg">
                <Label htmlFor="show_rda_sodium" className="cursor-pointer">Sodium</Label>
                <Switch
                  id="show_rda_sodium"
                  checked={defaults.show_rda_sodium !== false}
                  onCheckedChange={(checked) => updateField('show_rda_sodium', checked)}
                />
              </div>
            </div>
            
            <p className="text-xs text-[#795548] mt-4">Note: "of which sugars" and "Trans Fat" never show RDA as per FSSAI guidelines</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default LabelDefaults;
